/*
 * DEOXY Size & Fit — metaobject-backed drawer and unit controls.
 * Variant labels remain native Shopify/Fabric output; this module only owns
 * Size & Fit presentation behaviour and garment-measurement unit conversion.
 */
(() => {
  if (window.__DEOXY_SIZE_FIT__) return;
  window.__DEOXY_SIZE_FIT__ = true;

  const DRAWER_TRANSITION_MS = 240;
  const drawerCloseTimers = new WeakMap();

  const trimNumber = (n) => {
    const fixed = Number(n).toFixed(2);
    return fixed.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  };

  function stripLegacyRegionalColumns(table) {
    if (!(table instanceof HTMLTableElement)) return;
    if (table.dataset.deoxyRegionalColumnsStripped === 'true') return;

    const headerRow = table.querySelector('thead tr:first-child');
    if (!(headerRow instanceof HTMLTableRowElement)) {
      table.dataset.deoxyRegionalColumnsStripped = 'true';
      return;
    }

    const removeIndexes = Array.from(headerRow.children)
      .map((cell, index) => ({
        index,
        text: (cell.textContent || '').trim(),
      }))
      .filter(({ text }) => /^(UK|EU|US)(\s+SIZE)?$/i.test(text))
      .map(({ index }) => index)
      .sort((a, b) => b - a);

    for (const index of removeIndexes) {
      table.querySelectorAll('tr').forEach((row) => {
        row.children[index]?.remove();
      });
    }

    table.dataset.deoxyRegionalColumnsStripped = 'true';
  }

  function prepareGarmentTables(root = document) {
    root.querySelectorAll('[data-deoxy-garment-measurements] table').forEach((table) => {
      if (!(table instanceof HTMLTableElement)) return;

      // Regional UK/EU/US equivalence belongs in the BODY size table above.
      // The garment table should contain only the actual Alpha size and
      // physical finished-garment measurements for this specific style.
      stripLegacyRegionalColumns(table);

      if (table.dataset.deoxyUnitReady === 'true') return;

      const headers = Array.from(table.querySelectorAll('thead th')).map((th) => th.textContent.trim());
      table.querySelectorAll('tbody tr').forEach((row) => {
        Array.from(row.children).forEach((cell, index) => {
          if (!(cell instanceof HTMLTableCellElement)) return;
          const header = headers[index] || '';
          if (/size|uk|eu|us/i.test(header)) return;
          const raw = cell.textContent.trim().replace(/,/g, '');
          if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return;
          const cm = Number(raw);
          cell.dataset.deoxyCm = raw;
          cell.dataset.deoxyIn = trimNumber(cm / 2.54);
        });
      });
      table.dataset.deoxyUnitReady = 'true';
    });
  }

  function syncGarmentTables(root, unit) {
    prepareGarmentTables(root);
    root.querySelectorAll('[data-deoxy-garment-measurements] td[data-deoxy-cm]').forEach((cell) => {
      cell.textContent = unit === 'in' ? cell.dataset.deoxyIn : cell.dataset.deoxyCm;
    });
    root.querySelectorAll('[data-deoxy-garment-measurements] *').forEach((el) => {
      if (el.children.length !== 0) return;
      const text = el.textContent.trim();
      if (/^measurements in cm$/i.test(text)) {
        el.dataset.deoxyUnitCopy = 'true';
      }
      if (el.dataset.deoxyUnitCopy === 'true') {
        el.textContent = unit === 'in' ? 'Measurements in inches' : 'Measurements in cm';
      }
    });
  }

  function setUnit(root, unit) {
    if (!(root instanceof HTMLElement)) return;
    root.dataset.unit = unit;
    root.querySelectorAll('[data-deoxy-unit-button]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.deoxyUnitButton === unit ? 'true' : 'false');
    });
    syncGarmentTables(root, unit);
  }

  /* ------------------------------------------------------------------------
     DEOXY — iOS Safari mobile drawer stabilisation
     Safari can report a stale dynamic viewport on the very first showModal().
     We capture the real visual viewport before opening, keep it synced while
     the dialog is open, and reset the inner panel to the top after layout.
     ------------------------------------------------------------------------ */
  function isMobileSizeFit() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function syncMobileDialogViewport(dialog) {
    if (!(dialog instanceof HTMLDialogElement) || !isMobileSizeFit()) return;

    const viewport = window.visualViewport;
    const height = viewport ? viewport.height : window.innerHeight;
    const offsetTop = viewport ? viewport.offsetTop : 0;

    if (Number.isFinite(height) && height > 0) {
      dialog.style.setProperty('--dx-fit-mobile-vh', `${Math.round(height)}px`);
    }

    if (Number.isFinite(offsetTop)) {
      dialog.style.setProperty('--dx-fit-mobile-top', `${Math.round(offsetTop)}px`);
    }
  }

  function resetMobileDrawer(dialog) {
    if (!(dialog instanceof HTMLDialogElement) || !isMobileSizeFit()) return;

    const panel = dialog.querySelector('.dx-fit-drawer__panel');
    if (!(panel instanceof HTMLElement)) return;

    const settle = () => {
      syncMobileDialogViewport(dialog);
      dialog.scrollTop = 0;
      panel.scrollTop = 0;
    };

    settle();

    /* Two frames lets iOS Safari finish moving the dialog into the top layer
       and updating the visual viewport before we make the final correction. */
    requestAnimationFrame(() => {
      settle();
      requestAnimationFrame(settle);
    });
  }

  function syncOpenMobileDrawers() {
    if (!isMobileSizeFit()) return;

    document
      .querySelectorAll('dialog.dx-fit-drawer[open]')
      .forEach((dialog) => syncMobileDialogViewport(dialog));
  }

  function openDrawer(button) {
    const id = button.getAttribute('aria-controls');
    if (!id) return;

    const dialog = document.getElementById(id);
    if (!(dialog instanceof HTMLDialogElement)) return;

    const pendingClose = drawerCloseTimers.get(dialog);
    if (pendingClose) {
      window.clearTimeout(pendingClose);
      drawerCloseTimers.delete(dialog);
    }

    /* DEOXY — the shared product garment-measurement component is an
       accordion on the full Size & Fit page. Inside the product drawer
       we always want its measurement table expanded and visible. */
    dialog
      .querySelectorAll('[data-deoxy-garment-measurements] details')
      .forEach((details) => {
        details.open = true;
      });

    /* Capture iPhone Safari's REAL visible viewport before showModal().
       This prevents the first-open sheet from being positioned against
       a stale layout viewport until the user scrolls. */
    syncMobileDialogViewport(dialog);

    dialog.classList.remove('dx-fit-drawer--visible');
    if (!dialog.open) dialog.showModal();

    resetMobileDrawer(dialog);

    /* showModal() places the dialog in the top layer synchronously. Waiting
       two animation frames guarantees the off-canvas start state is painted
       before transitioning the single panel into view. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (dialog.open) dialog.classList.add('dx-fit-drawer--visible');
      });
    });

    button.setAttribute('aria-expanded', 'true');
    setUnit(dialog.querySelector('[data-deoxy-unit-root]') || dialog, 'cm');
  }

  function requestDrawerClose(dialog) {
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    dialog.classList.remove('dx-fit-drawer--visible');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      dialog.close();
      return;
    }

    const existing = drawerCloseTimers.get(dialog);
    if (existing) window.clearTimeout(existing);

    const timer = window.setTimeout(() => {
      drawerCloseTimers.delete(dialog);
      if (dialog.open) dialog.close();
    }, DRAWER_TRANSITION_MS);
    drawerCloseTimers.set(dialog, timer);
  }

  function closeDrawer(button) {
    const dialog = button.closest('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;
    requestDrawerClose(dialog);
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const unitButton = event.target.closest('[data-deoxy-unit-button]');
    if (unitButton instanceof HTMLButtonElement) {
      const root = unitButton.closest('[data-deoxy-unit-root]');
      if (root instanceof HTMLElement) setUnit(root, unitButton.dataset.deoxyUnitButton || 'cm');
      return;
    }

    const open = event.target.closest('[data-deoxy-fit-open]');
    if (open instanceof HTMLButtonElement) {
      openDrawer(open);
      return;
    }

    const close = event.target.closest('[data-deoxy-fit-close]');
    if (close instanceof HTMLButtonElement) {
      closeDrawer(close);
      return;
    }

    const height = event.target.closest('[data-deoxy-height-button]');
    if (height instanceof HTMLButtonElement) {
      const root = height.closest('[data-deoxy-height-guide]');
      if (!root) return;
      root.querySelectorAll('[data-deoxy-height-button]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      height.setAttribute('aria-pressed', 'true');
      const result = root.querySelector('[data-deoxy-height-result]');
      if (result) result.textContent = height.dataset.deoxyHeightCopy || '';
    }
  });

  document.addEventListener('cancel', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-deoxy-fit-dialog]')) return;
    event.preventDefault();
    requestDrawerClose(dialog);
  }, true);

  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-deoxy-fit-dialog]')) return;

    const pendingClose = drawerCloseTimers.get(dialog);
    if (pendingClose) {
      window.clearTimeout(pendingClose);
      drawerCloseTimers.delete(dialog);
    }
    dialog.classList.remove('dx-fit-drawer--visible');

    const opener = document.querySelector(`[aria-controls="${CSS.escape(dialog.id)}"]`);
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      if (opener instanceof HTMLElement) opener.focus({ preventScroll: true });
    }
  }, true);

  document.addEventListener('click', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-deoxy-fit-dialog]')) return;
    const panel = dialog.querySelector('.dx-fit-drawer__panel');
    if (panel && !panel.contains(event.target)) requestDrawerClose(dialog);
  });

  function init(root = document) {
    root.querySelectorAll('[data-deoxy-unit-root]').forEach((unitRoot) => {
      if (unitRoot instanceof HTMLElement) setUnit(unitRoot, unitRoot.dataset.unit || 'cm');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }


  /* Keep an already-open mobile drawer attached to Safari's visual viewport
     while the browser bars expand/collapse or the device orientation changes. */
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncOpenMobileDrawers, { passive: true });
    window.visualViewport.addEventListener('scroll', syncOpenMobileDrawers, { passive: true });
  }
  window.addEventListener('orientationchange', () => {
    requestAnimationFrame(syncOpenMobileDrawers);
  }, { passive: true });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('[data-deoxy-garment-measurements]') || node.querySelector('[data-deoxy-garment-measurements]')) {
          prepareGarmentTables(node);
        }
      });
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();